/**
 * Farm Context
 * Provides multi-farm support for users who belong to multiple farms
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import {
  getUserFarms,
  setActiveFarm as setActiveFarmInDb,
  getActiveFarmId,
  getUserFarmDetails,
  initializeOwnFarm,
} from "../api/firestore/services/farmService";

// Role constants
export const ROLES = {
  ADMIN: 1,
  MANAGER: 2,
  WORKER: 3,
};

export const ROLE_NAMES = {
  1: "Admin",
  2: "Manager",
  3: "Worker",
};

// Create the context — use undefined as default so useFarm() can detect missing provider
const FarmContext = createContext(undefined);

/**
 * Farm Context Provider
 */
export function FarmProvider({ children }) {
  const { currentUser, userData } = useAuth();

  // State
  const [currentFarmId, setCurrentFarmId] = useState(null);
  const [currentFarmData, setCurrentFarmData] = useState(null);
  const [userFarms, setUserFarms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);

  // Derived state
  const isOwnFarm = !currentFarmId || currentFarmId === currentUser?.uid;
  const userRole = isOwnFarm ? ROLES.ADMIN : (currentFarmData?.role || ROLES.WORKER);

  // Permission helpers
  const canManageTeam = userRole === ROLES.ADMIN;
  const canEditData = userRole <= ROLES.MANAGER;
  const isReadOnly = userRole === ROLES.WORKER;

  /**
   * Load user's farms
   */
  const loadFarms = useCallback(async () => {
    if (!currentUser?.uid) {
      setUserFarms([]);
      setIsLoading(false);
      return;
    }

    try {
      const farms = await getUserFarms(currentUser.uid);

      // Add own farm if not in list
      const hasOwnFarm = farms.some((f) => f.farmId === currentUser.uid);
      if (!hasOwnFarm) {
        // Initialize own farm entry
        const farmName = userData?.farmName || userData?.Name || "My Farm";
        await initializeOwnFarm(currentUser.uid, farmName);

        farms.unshift({
          id: currentUser.uid,
          farmId: currentUser.uid,
          farmName: farmName,
          role: ROLES.ADMIN,
          isOwner: true,
          isPrimary: true,
        });
      }

      setUserFarms(farms);
    } catch (error) {
      console.error("Error loading farms:", error);
      setUserFarms([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.uid, userData?.farmName, userData?.Name]);

  /**
   * Load active farm on mount
   */
  useEffect(() => {
    const loadActiveFarm = async () => {
      if (!currentUser?.uid) {
        setCurrentFarmId(null);
        setCurrentFarmData(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Load farms first
        await loadFarms();

        // Get active farm ID
        const activeFarmId = await getActiveFarmId(currentUser.uid);
        setCurrentFarmId(activeFarmId);

        // Load farm details if not own farm
        if (activeFarmId && activeFarmId !== currentUser.uid) {
          const farmDetails = await getUserFarmDetails(currentUser.uid, activeFarmId);
          setCurrentFarmData(farmDetails);
        } else {
          setCurrentFarmData(null);
        }
      } catch (error) {
        console.error("Error loading active farm:", error);
        setCurrentFarmId(null);
        setCurrentFarmData(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadActiveFarm();
  }, [currentUser?.uid, loadFarms]);

  /**
   * Switch to a different farm
   */
  const switchFarm = useCallback(
    async (farmId) => {
      if (!currentUser?.uid) return;

      // If switching to own farm, use null
      const targetFarmId = farmId === currentUser.uid ? null : farmId;

      // Don't switch if already on this farm
      if (targetFarmId === currentFarmId) return;

      try {
        setIsSwitching(true);

        // Update in database
        await setActiveFarmInDb(currentUser.uid, targetFarmId);

        // Update local state
        setCurrentFarmId(targetFarmId);

        // Load farm details if not own farm
        if (targetFarmId) {
          const farmDetails = await getUserFarmDetails(currentUser.uid, targetFarmId);
          setCurrentFarmData(farmDetails);
        } else {
          setCurrentFarmData(null);
        }
      } catch (error) {
        console.error("Error switching farm:", error);
        throw error;
      } finally {
        setIsSwitching(false);
      }
    },
    [currentUser?.uid, currentFarmId]
  );

  /**
   * Refresh the farms list
   */
  const refreshFarms = useCallback(async () => {
    await loadFarms();
  }, [loadFarms]);

  // Get the effective farm ID (for data queries)
  const effectiveFarmId = currentFarmId || currentUser?.uid;

  // Context value
  const value = {
    // Current farm state
    currentFarmId: effectiveFarmId,
    currentFarmData,
    userFarms,
    isOwnFarm,
    userRole,

    // Loading states
    isLoading,
    isSwitching,

    // Actions
    switchFarm,
    refreshFarms,

    // Permission helpers
    canManageTeam,
    canEditData,
    isReadOnly,
  };

  return <FarmContext.Provider value={value}>{children}</FarmContext.Provider>;
}

/**
 * Hook to use the Farm context
 */
export function useFarm() {
  const context = useContext(FarmContext);
  if (!context) {
    throw new Error("useFarm must be used within a FarmProvider");
  }
  return context;
}

export default FarmContext;
